import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../CustomButton';
import { downloadText } from './importReportView';
import type { ViewedSetupProgress } from '../../hooks/useSetupProgress';
import { OpenPoint, OpenPointSeverity, SEVERITY_ORDER, openPointsText, setupStateNotice } from './openPoints';

const card = 'bg-text-contrast rounded-[5px] shadow-sm px-5 py-4 flex flex-col gap-3';

/** Grayscale only (CLAUDE.md): Kritisch is the filled badge, Wichtig outlined, Optional muted. */
const BADGE: Record<OpenPointSeverity, string> = {
    critical: 'bg-foreground text-text-contrast',
    important: 'border border-foreground/60 text-foreground',
    optional: 'border border-foreground/20 text-foreground/60',
};

/**
 * "Offene Punkte": what the file did not set up, Kritisch first, each with
 * its consequence — on the preview and after Übernehmen. Copyable and
 * downloadable as plain text, so it can go into a mail or a checklist.
 */
const OpenPointsList: React.FC<{
    points: OpenPoint[];
    hint: string;
    /** The account checklist's state (onboarding import); absent where no account point is listed. */
    setupState?: ViewedSetupProgress['state'];
    /** The heading of the copied and downloaded text; the onboarding import's by default. */
    textTitle?: string;
}> = ({
    points,
    hint,
    setupState = 'ready',
    textTitle,
}) => {
    const { t } = useTranslation();
    // While the account is loading or its read failed, the account and Benutzungsordnung points are not in
    // the list: say so instead of letting an empty list read as "nothing open".
    const notice = setupStateNotice(setupState, t);
    const [copied, setCopied] = useState(false);
    const [copyFailed, setCopyFailed] = useState(false);
    const text = () => openPointsText(points, t, notice, textTitle);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text());
            setCopied(true);
            setCopyFailed(false);
        } catch {
            setCopyFailed(true);
        }
    };

    return (
        <section className={card}>
            <h2 className={'text-base font-semibold text-foreground'}>{t('onboarding_import.open_points.title')}</h2>
            <p className={'text-sm text-foreground/80'}>{hint}</p>
            {notice && <p role={setupState === 'failed' ? 'alert' : undefined} className={'text-sm text-foreground'}>{notice}</p>}
            {!notice && points.length === 0 && <p className={'text-sm text-foreground/60'}>{t('onboarding_import.open_points.none')}</p>}
            {SEVERITY_ORDER.map((severity) => {
                const group = points.filter((p) => p.severity === severity);
                if (group.length === 0) return null;
                return (
                    <div key={severity} className={'flex flex-col gap-2'}>
                        <div className={'flex items-center gap-2'}>
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${BADGE[severity]}`}>
                                {t(`onboarding_import.open_points.severity.${severity}`)}
                            </span>
                            <span className={'text-xs text-foreground/60'}>{t(`onboarding_import.open_points.severity_hint.${severity}`)}</span>
                        </div>
                        <ul className={'flex flex-col gap-2 pl-1 text-sm'}>
                            {group.map((p) => (
                                <li key={`${p.code}-${p.title}-${p.detail ?? ''}`} className={'flex flex-col'}>
                                    <span className={'text-foreground'}>
                                        <span className={'font-medium'}>{p.title}</span>
                                        {p.detail ? `: ${p.detail}` : ''}
                                    </span>
                                    <span className={'text-foreground/70'}>{p.consequence}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
            {points.length > 0 && (
                <div className={'flex flex-wrap gap-3'}>
                    <CustomButton
                        title={t(copied ? 'onboarding_import.open_points.copied' : 'onboarding_import.open_points.copy')}
                        isFilled={false}
                        onClick={copy}
                    />
                    <CustomButton
                        title={t('onboarding_import.open_points.download')}
                        isFilled={false}
                        onClick={() => downloadText(text(), t('onboarding_import.open_points.file'), 'text/plain')}
                    />
                </div>
            )}
            {copyFailed && <p role={'alert'} className={'text-sm text-foreground'}>{t('onboarding_import.open_points.copy_failed')}</p>}
        </section>
    );
};

export default OpenPointsList;
